import { createSlice } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";

const FILES_CONTEXT = {
  ALL_POSTINGS: "All Postings",
  DISBURSEMENT_PAYMENT: "Disbursement-Payment",
  BILL: "Bill",
  CURRENT_USER: "CurrentUser",
  EDIT_PROFILE: "EditProfile",
  OFFICE_CREDIT_ALLOCATION: "OfficeCreditandAllocation",
  OFFICE_POSTING: "OfficePosting",
  CLIENT_POSTING: "ClientPosting",
  CHIT_REQUEST: "Chit Request",
};

const FILES_CONTEXT_TO_STATE_KEY = {
  [FILES_CONTEXT.ALL_POSTINGS]: "files",
  [FILES_CONTEXT.DISBURSEMENT_PAYMENT]: "disbPaymentFiles",
  [FILES_CONTEXT.BILL]: "billFiles",
  [FILES_CONTEXT.OFFICE_CREDIT_ALLOCATION]: "officeBillandAllocationFiles",
  [FILES_CONTEXT.OFFICE_POSTING]: "officePostingFiles",
  [FILES_CONTEXT.CLIENT_POSTING]: "clientPostingFiles",
  [FILES_CONTEXT.CHIT_REQUEST]: "chitRequestFiles",
};

const initialState = {
  isError: false,
  isSuccess: false,
  isLoading: undefined,
  files: [],
  disbPaymentFiles: [],
  billFiles: [],
  officeBillandAllocationFiles: [],
  officePostingFiles: [],
  clientPostingFiles: [],
  chitRequestFiles: [],
};

const filesDownloadSlice = createSlice({
  name: "filesDownload",
  initialState,
  reducers: {
    addFiles: {
      reducer: (state, action) => {
        const { contextKey, file } = action.payload;
        state.isError = false;
        state.isSuccess = true;
        state.isLoading = false;
        if (contextKey === FILES_CONTEXT.CURRENT_USER) return;
        const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
        if (stateKey && state[stateKey]) {
          state[stateKey].push(file);
        }
      },
      prepare: (contextKey, datacomplete) => ({
        payload: { contextKey, file: datacomplete?.data?.data ?? datacomplete },
      }),
    },

    removeFile: {
      reducer: (state, action) => {
        const { contextKey, filename } = action.payload;
        const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
        if (stateKey && state[stateKey]) {
          state[stateKey] = state[stateKey].filter(
            (item) => item.filename !== filename,
          );
        }
      },
      prepare: (contextKey, filename) => ({
        payload: { contextKey, filename },
      }),
    },

    clearFile: (state, action) => {
      state.isLoading = false;
      const stateKey = FILES_CONTEXT_TO_STATE_KEY[action.payload];
      if (stateKey && state[stateKey]) {
        state[stateKey] = [];
      }
    },

    clearFiles: (state) => {
      state.files = [];
    },

    clearFilesDispPayment: (state) => {
      state.disbPaymentFiles = [];
    },
  },
});

const {
  addFiles,
  removeFile,
  clearFile,
  clearFiles,
  clearFilesDispPayment,
} = filesDownloadSlice.actions;

const filesDownloadReducer = filesDownloadSlice.reducer;

const useFiles = (contextKey) => {
  const dispatch = useDispatch();
  const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
  const files = useSelector((state) =>
    stateKey ? state.filesDownload?.[stateKey] ?? [] : []
  );
  const isLoading = useSelector((state) => state.filesDownload?.isLoading);
  const isError = useSelector((state) => state.filesDownload?.isError);
  const isSuccess = useSelector((state) => state.filesDownload?.isSuccess);

  const addFile = (datacomplete) => {
    dispatch(addFiles(contextKey, datacomplete));
  };

  const removeFileByName = (filename) => {
    dispatch(removeFile(contextKey, filename));
  };

  const clearFilesForContext = () => {
    dispatch(clearFile(contextKey));
  };

  const clearAllFiles = () => {
    dispatch(clearFiles());
  };

  const clearDisbursementFiles = () => {
    dispatch(clearFilesDispPayment());
  };

  return {
    files,
    isLoading,
    isError,
    isSuccess,
    addFile,
    removeFile: removeFileByName,
    clearFile: clearFilesForContext,
    clearAllFiles,
    clearDisbursementFiles,
  };
};

export {
  filesDownloadReducer,
  addFiles,
  removeFile,
  clearFiles,
  clearFilesDispPayment,
  clearFile,
  useFiles,
  FILES_CONTEXT
};
